import React, { useCallback, useEffect, useState } from 'react';
import SafeIcon from '../../components/common/SafeIcon';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../lib/supabase';

export default function OgDefaultsPage() {
  const { can } = useAuth();
  const canManage = can('manage_users');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [form, setForm] = useState({
    title: '',
    description: '',
    imageUrl: '',
  });

  const loadDefaults = useCallback(async () => {
    setLoading(true);
    setError('');
    const { data, error: fetchError } = await supabase.rpc('gfi_og_defaults');

    if (fetchError) {
      setError(fetchError.message);
    } else if (data) {
      setForm({
        title: data.title ?? '',
        description: data.description ?? '',
        imageUrl: data.image_url ?? '',
      });
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (canManage) loadDefaults();
  }, [canManage, loadDefaults]);

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    setSuccess('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    const { data, error: saveError } = await supabase.rpc('gfi_superadmin_update_og_defaults', {
      p_title: form.title.trim(),
      p_description: form.description.trim(),
      p_image_url: form.imageUrl.trim(),
    });

    setSaving(false);

    if (saveError) {
      setError(saveError.message);
      return;
    }

    if (data) {
      setForm({
        title: data.title ?? form.title,
        description: data.description ?? form.description,
        imageUrl: data.image_url ?? form.imageUrl,
      });
    }
    setSuccess('Configuración OG predeterminada actualizada.');
  };

  if (!canManage) {
    return null;
  }

  return (
    <div className="min-h-screen bg-[var(--bg-base)] pb-24 md:pb-12">
      <div className="bg-[var(--bg-surface)] border-b border-[var(--border-subtle)] pt-12 pb-8 px-6 md:px-12">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-3xl md:text-4xl font-black text-[var(--text-main)] uppercase italic tracking-tight mb-2">
            Vista Previa OG
          </h1>
          <p className="text-xs text-[var(--text-secondary)] uppercase tracking-widest">
            Superadmin — imagen, título y descripción predeterminados para páginas sin OG propio
          </p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-10">
        {loading ? (
          <p className="text-sm text-[var(--text-secondary)]">Cargando configuración...</p>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded-2xl p-6 md:p-8 space-y-5">
              <div>
                <label
                  htmlFor="og-title"
                  className="block text-[10px] font-black uppercase tracking-widest text-[var(--text-secondary)] mb-2"
                >
                  Título predeterminado
                </label>
                <input
                  id="og-title"
                  type="text"
                  required
                  value={form.title}
                  onChange={handleChange('title')}
                  className="w-full bg-[var(--bg-base)] border border-[var(--border-subtle)] rounded-xl px-4 py-3 text-sm text-[var(--text-main)] focus:outline-none focus:border-brand-cyan"
                />
              </div>

              <div>
                <label
                  htmlFor="og-description"
                  className="block text-[10px] font-black uppercase tracking-widest text-[var(--text-secondary)] mb-2"
                >
                  Descripción predeterminada
                </label>
                <textarea
                  id="og-description"
                  required
                  rows={3}
                  value={form.description}
                  onChange={handleChange('description')}
                  className="w-full bg-[var(--bg-base)] border border-[var(--border-subtle)] rounded-xl px-4 py-3 text-sm text-[var(--text-main)] focus:outline-none focus:border-brand-cyan resize-y"
                />
              </div>

              <div>
                <label
                  htmlFor="og-image"
                  className="block text-[10px] font-black uppercase tracking-widest text-[var(--text-secondary)] mb-2"
                >
                  URL de imagen predeterminada
                </label>
                <input
                  id="og-image"
                  type="url"
                  required
                  value={form.imageUrl}
                  onChange={handleChange('imageUrl')}
                  className="w-full bg-[var(--bg-base)] border border-[var(--border-subtle)] rounded-xl px-4 py-3 text-sm text-[var(--text-main)] focus:outline-none focus:border-brand-cyan"
                />
              </div>

              {form.imageUrl && (
                <div>
                  <p className="text-[10px] font-black uppercase tracking-widest text-[var(--text-secondary)] mb-2">
                    Vista previa de imagen
                  </p>
                  <div className="rounded-xl overflow-hidden border border-[var(--border-subtle)] aspect-[1200/630] max-w-md bg-[var(--bg-base)]">
                    <img
                      src={form.imageUrl}
                      alt="Vista previa OG"
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  </div>
                </div>
              )}
            </div>

            {error && (
              <div className="flex items-center gap-2 text-sm text-rose-600">
                <SafeIcon name="AlertCircle" className="w-4 h-4 shrink-0" />
                {error}
              </div>
            )}

            {success && (
              <div className="flex items-center gap-2 text-sm text-brand-green">
                <SafeIcon name="CheckCircle" className="w-4 h-4 shrink-0" />
                {success}
              </div>
            )}

            <button
              type="submit"
              disabled={saving}
              className="px-6 py-3 gfi-gradient-bg text-brand-navy rounded-xl text-[10px] font-black uppercase tracking-widest disabled:opacity-50"
            >
              {saving ? 'Guardando...' : 'Guardar cambios'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
